import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { getLeads, updateLeadStatus, type Lead } from "@/Api/leads";
import { getLeadSteps, type LeadStep } from "@/Api/lead-steps";
import { toast } from "sonner";
import PageTemplate from "@/components/PageTemplate";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarClock,
  Phone,
  Mail,
  Plus,
  Tag,
  User,
  GripVertical,
  FileBadge,
  Settings,
} from "lucide-react";
import { CreateLeadDialog } from "../components/CreateLeadDialog";
import { EditLeadDialog } from "../components/EditLeadDialog";
import { Spinner } from "@/components/ui/spinner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Mask } from "@/utils/mask";
import { useNavigate } from "react-router-dom";

import "./kanban.css";
import { useAuth, useTypeUser } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export function PageList() {
  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [steps, setSteps] = useState<{
    [key: string]: LeadStep;
  }>({});
  const [stepIds, setStepIds] = useState<string[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const authOnlyClient = useTypeUser(2);
  const { hasPermission } = usePermissions();
  const user = useAuth();

  const loadSteps = async () => {
    try {
      const response = await getLeadSteps();
      if (response.status) {
        const sortedSteps = response?.response?.sort(
          (a, b) => a.order - b.order
        );

        const stepsObj = sortedSteps?.reduce(
          (acc, step) => ({
            ...acc,
            [step.id]: step,
          }),
          {}
        );

        setSteps(stepsObj);
        setStepIds(sortedSteps?.map((step) => step.id.toString()) || []);
      }
    } catch (error) {}
  };

  const loadLeads = async () => {
    setIsLoading(true);
    try {
      const response = await getLeads(
        user?.user?.seller?.id
      );
      if (response.status) {
        setLeads(response.response.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([loadSteps(), loadLeads()]);
  }, []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch (e) {
      return dateString;
    }
  };

  const formatPhone = (phone: string) => {
    return Mask.maskPhone(phone || "");
  };

  const formatDocument = (document: string) => {
    return document ? Mask.maskCpfOrCnpj(document) : "";
  };

  const handleDragStart = () => {
    setIsDragging(true);
    document.body.classList.add("dragging");
  };

  const handleDragEnd = async (result: any) => {
    setIsDragging(false);
    document.body.classList.remove("dragging");

    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStepId = parseInt(destination.droppableId);

    const updatedLeads = leads.map((lead) => {
      if (lead.uuid === draggableId) {
        return {
          ...lead,
          lead_step_id: newStepId,
          step_name: steps[newStepId]?.name,
        };
      }
      return lead;
    });

    setLeads(updatedLeads);

    try {
      const result = await updateLeadStatus(draggableId, newStepId);

      if (!result.status) {
        loadLeads();
      } else {
        toast.success("Status atualizado", {
          description: `Lead movido para ${steps[newStepId].name}`,
        });
      }
    } catch (error) {
      loadLeads();
    }
  };

  const handleOpenEditDialog = (lead: Lead) => {
    if (isDragging) return;

    setSelectedLead(lead);
    setEditDialogOpen(true);
  };

  const handleLeadUpdated = () => {
    loadLeads();
  };

  if (isLoading) {
    return (
      <PageTemplate>
        <div className="flex justify-center items-center h-64">
          <Spinner className="w-8 h-8" />
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <If condition={hasPermission("view-leads-management")}>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">CRM - Gestão de Leads</h1>
          <div className="flex gap-2">
            <If condition={hasPermission("configuration-lead-columns")}>
              {authOnlyClient(() => (
                <Button
                  variant="outline"
                  onClick={() => navigate("/crm/settings")}
                >
                  <Settings className="mr-2 h-4 w-4" /> Configurar Colunas
                </Button>
              ))}
            </If>
            <If condition={hasPermission("create-lead")}>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Novo Lead
              </Button>
            </If>
          </div>
        </div>

        <div className="overflow-x-auto pb-6">
          <DragDropContext
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <div className="flex gap-4 min-w-[1200px]">
              {stepIds.map((stepId) => {
                const step = steps[stepId];
                const leadsInStep = leads.filter(
                  (lead) => lead.lead_step_id.toString() === stepId
                );

                if (!step) return null;

                return (
                  <div key={stepId} className="flex-1 min-w-[300px]">
                    <Card className="h-full">
                      <CardHeader
                        className="pb-2"
                        style={{
                          backgroundColor: step.color || "#f3f4f6",
                          color: getContrastColor(step.color),
                        }}
                      >
                        <CardTitle className="text-sm font-medium flex justify-between items-center">
                          <span>{step.name}</span>
                          <span className="px-2 py-1 rounded-full bg-white/80 text-gray-900 text-xs font-semibold">
                            {leadsInStep.length}
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-2 h-[calc(100vh-220px)] overflow-y-auto">
                        <Droppable droppableId={stepId} type="LEAD">
                          {(provided, snapshot) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className={`kanban-column min-h-[200px] rounded-md ${
                                snapshot.isDraggingOver
                                  ? "kanban-column-active"
                                  : ""
                              }`}
                            >
                              {leadsInStep.length === 0 ? (
                                <div className="flex items-center justify-center h-24 border border-dashed rounded-md border-muted-foreground/20 text-muted-foreground text-sm">
                                  Arraste leads para esta coluna
                                </div>
                              ) : (
                                leadsInStep.map((lead, index) => (
                                  <Draggable
                                    key={lead.uuid}
                                    draggableId={lead.uuid}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`kanban-item ${
                                          snapshot.isDragging
                                            ? "kanban-item-dragging"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          handleOpenEditDialog(lead)
                                        }
                                      >
                                        <div className="flex justify-between items-start">
                                          <h3 className="font-medium text-sm truncate">
                                            {lead.name}
                                          </h3>
                                          <div
                                            {...provided.dragHandleProps}
                                            className="kanban-handle"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <GripVertical className="h-4 w-4 text-gray-400" />
                                          </div>
                                        </div>

                                        <div className="mt-2 space-y-1">
                                          <div className="flex items-center text-xs text-muted-foreground">
                                            <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                                            <span className="truncate">
                                              {lead.email}
                                            </span>
                                          </div>

                                          <div className="flex items-center text-xs text-muted-foreground">
                                            <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                                            <span>
                                              {formatPhone(lead.phone)}
                                            </span>
                                          </div>

                                          {lead.document && (
                                            <div className="flex items-center text-xs text-muted-foreground">
                                              <FileBadge className="h-3 w-3 mr-1 flex-shrink-0" />
                                              <span>
                                                {formatDocument(lead.document)}
                                              </span>
                                            </div>
                                          )}

                                          {lead.client_origin && (
                                            <div className="flex items-center text-xs text-muted-foreground">
                                              <Tag className="h-3 w-3 mr-1 flex-shrink-0" />
                                              <span className="truncate">
                                                {lead.client_origin.name}
                                              </span>
                                            </div>
                                          )}

                                          {lead?.seller && (
                                            <div className="flex items-center text-xs text-muted-foreground">
                                              <User className="h-3 w-3 mr-1 flex-shrink-0" />
                                              <span className="truncate">
                                                {lead?.seller?.user?.name}
                                              </span>
                                            </div>
                                          )}

                                          <div className="flex items-center text-xs text-muted-foreground pt-1">
                                            <CalendarClock className="h-3 w-3 mr-1 flex-shrink-0" />
                                            <span>
                                              {formatDate(lead.created_at)}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="mt-2 text-xs text-blue-500 hover:underline">
                                          Clique para editar
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </div>

        <CreateLeadDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onLeadCreated={handleLeadUpdated}
        />

        {selectedLead && (
          <EditLeadDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            lead={selectedLead}
            onLeadUpdated={handleLeadUpdated}
          />
        )}
      </If>
    </PageTemplate>
  );
}

function getContrastColor(hexcolor?: string) {
  if (!hexcolor) return "#000000";

  const hex = hexcolor.replace("#", "");

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}
